import { PlayerClubIteration, PlayerId, PlayerLocality, PlayerOverview, PlayerRole } from '../types/player';
import { StringToAnyMap, StringToNumberMap } from '../types/common';
import { PlayerAlertsQueryOptions } from '../types/scouting';

import { playerLocalityToSortOrder } from '../static/constants/playerLocalities';
import { playerRoleToSortOrder } from '../static/constants/playerRoles';

import { getAge } from './ageUtils';
import { getMonthsLeftOfContract } from './contractUtils';
import { CompetitionMap } from '../types/competition';
import { PositionGroupKey } from '../types/position';


// todo: investigate if this is needed
// get playerId, where a number is returned if possible, else string
export const getStrictPlayerId = (playerId: PlayerId) => {
  const numberId = Number(playerId);
  if (isNaN(numberId)) {
    return playerId;
  }
  return numberId;
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidPlayerLocality = (playerLocality: any): playerLocality is PlayerLocality => {
  return playerLocality in playerLocalityToSortOrder;
};


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidPlayerRole = (role: any): role is PlayerRole => {
  return role in playerRoleToSortOrder;
};


// get sort distance between two players based on role (descending, where order of roles defined above)
export const getSortDistanceRole = (a: StringToAnyMap, b: StringToAnyMap, property: string) => {
  if (!b[property] || !isValidPlayerRole(b[property])) {
    return -1;
  }
  if (!a[property] || !isValidPlayerRole(a[property])) {
    return 1;
  }
  return playerRoleToSortOrder[b[property]] - playerRoleToSortOrder[a[property]];
};


// get a value between 0 and 10 based on the locality
export const getLocalityIndexEquivalent = (locality: PlayerLocality): number | undefined => {
  if (!locality) return undefined;

  const localityToIndex: StringToNumberMap = {
    'local': 10,
    'national': 5,
    'international': 0,
  };

  return localityToIndex[locality];
};


// get the iterations matching the given constraints, and the best selected club iteration id
// if currentSelectedClubIterationId is set and is in the resulting club iteration array, this is considered the best one
// otherwise, iterations are sorted by a weighted sum of minutes, type and recency
export const getIterationsAndSelectedClubIterationId = (
  clubIterationArray: PlayerClubIteration[], // assumed to be sorted by plot_date, with more recent iterations first
  competitions: CompetitionMap,
  selectedPositionGroupKey?: PositionGroupKey,
  currentSelectedClubIterationId?: string | undefined,
  onlyConsiderLeagues?: boolean,
  onlyConsiderIterationsWithMoreThan180Minutes?: boolean,
): {
  newIterations: PlayerClubIteration[],
  newSelectedClubIterationId: string | undefined,
} => {

  const newIterations: PlayerClubIteration[] = [];

  let newSelectedClubIterationId: string | undefined = undefined;
  let newBestSelectedClubIterationScore = 0; // leagueWithEnoughMinutes: 4, cupWithEnoughMinutes: 3, league: 2, cup: 1

  let currentSelectionIsValid = false;

  clubIterationArray.forEach(iteration => {

    const eventDataAvailable = iteration.event_data_available;

    const competitionIsLeague = iteration.competition_id in competitions && competitions[iteration.competition_id].type === 'League';

    // if selectedPositionGroupKey is 'OVERALL', the corresponding key for iterations without 'OVERALL' will be the only position group for that iteration
    const positionGroupKeyToUse = selectedPositionGroupKey ?? 'OVERALL';
    const selectedPositionGroupKeyForIteration = positionGroupKeyToUse === 'OVERALL' && !iteration.position_group_stats?.OVERALL
      ? iteration.position_groups[0]
      : positionGroupKeyToUse;

    const minutesPlayed = iteration.position_group_stats?.[selectedPositionGroupKeyForIteration]?.minutes_played ?? 0;
    const hasAtLeast180Minutes = minutesPlayed >= 180;

    const seasonIsConstrainedByMinutes = onlyConsiderIterationsWithMoreThan180Minutes && !hasAtLeast180Minutes;
    const seasonIsConstrainedByLeague = onlyConsiderLeagues && !competitionIsLeague;

    // we only want to return iterations that match the given constraints
    if (!seasonIsConstrainedByMinutes && !seasonIsConstrainedByLeague) {
      newIterations.push(iteration);

      // we only allow iterations that have event data and more than 0 minutes played to be selected
      const isValidForSelection = eventDataAvailable && minutesPlayed > 0;
      if (isValidForSelection) {
        if (iteration.club_iteration_id === currentSelectedClubIterationId) {
          currentSelectionIsValid = true;
        }

        const currentScore = (hasAtLeast180Minutes ? 2 : 0) + (competitionIsLeague ? 2 : 1);
        if (currentScore > newBestSelectedClubIterationScore) {
          newSelectedClubIterationId = iteration.club_iteration_id;
          newBestSelectedClubIterationScore = currentScore;
        }
      }
    }
  });

  newSelectedClubIterationId = currentSelectedClubIterationId && currentSelectionIsValid
    ? currentSelectedClubIterationId
    : newSelectedClubIterationId;

  return {
    newIterations,
    newSelectedClubIterationId,
  };
};


// get the given player property value
export const getPlayerPropertyValue = (
  property: string,
  playerDocument?: StringToAnyMap | undefined,
  playerOverview?: StringToAnyMap | undefined,
  additionalPlayerOverview?: StringToAnyMap | undefined,
  valueTransformer?: (value: any) => any, // eslint-disable-line @typescript-eslint/no-explicit-any
  resultIfUndefined?: string,
) => {

  // user provided values have presedence
  if (playerDocument && property in playerDocument) {
    const value = playerDocument[property];
    if (valueTransformer) {
      return valueTransformer(value) ?? resultIfUndefined;
    }
    return value ?? resultIfUndefined;
  }

  if (playerOverview && property in playerOverview) {
    const value = playerOverview[property];
    if (valueTransformer) {
      return valueTransformer(value) ?? resultIfUndefined;
    }
    return value ?? resultIfUndefined;
  }

  if (additionalPlayerOverview && property in additionalPlayerOverview) {
    const value = additionalPlayerOverview[property];
    if (valueTransformer) {
      return valueTransformer(value) ?? resultIfUndefined;
    }
    return value ?? resultIfUndefined;
  }

  return resultIfUndefined;
};


// returns true if given players matches all given alert filters
export const playerMatchesAlertsFilters = (
  playerOverview: PlayerOverview,
  filters: PlayerAlertsQueryOptions,
  teams: StringToAnyMap | null,
) => {

  // maxAge?: number;
  // maxContractLength?: number;
  // includeUnknownContracts?: boolean;
  // primaryPositions?: string[];
  // roles?: string[];
  // positionGroup?: string | null;
  // competitions?: number[];
  // minConfidence?: number;
  // form?: string[];
  // playingTime?: string[];
  // countryCodes?: string[];
  // indexRequirements?: RatingRequirement[];
  // logisticTeamIds?: string[]; // either 'allTeams' or list of team ids

  if (filters.maxAge) {
    const playerAge = getAge(playerOverview.birth_date);
    if (playerAge === undefined || playerAge > filters['maxAge']) {
      return false;
    }
  }

  if (filters.maxContractLength) {
    const monthsLeft = getMonthsLeftOfContract(playerOverview.contract_expiration);
    if (monthsLeft !== undefined && monthsLeft > filters.maxContractLength) {
      return false;
    }
    if (monthsLeft === undefined && !filters.includeUnknownContracts) {
      return false;
    }
  }

  if (filters.primaryPositions) {
    if (filters.primaryPositions.every(position => !playerOverview.position_info.primary_positions.includes(position))) {
      return false;
    }
  }

  if (filters.positionGroup && filters.roles) {
    const positionGroupStats = playerOverview.position_group_stats?.[filters.positionGroup];
    if (!positionGroupStats || filters.roles.some(role => !positionGroupStats.custom_indexes?.[role]?.confidence)) {
      return false;
    }
  }

  if (filters.competitions) {
    if (!filters.competitions.includes(playerOverview.club.current_competition_id)) {
      return false;
    }
  }

  if (filters.confidenceRequirements) {
    let playerSatisfiesAllRequirements = true;
    filters.confidenceRequirements.forEach(requirement => {
      if (requirement.metric === 'skill_index' || requirement.metric === 'physical_index') {
        const positionGroupStats = playerOverview.position_group_stats?.['OVERALL'];
        if (!positionGroupStats || positionGroupStats[requirement.metric]?.confidence < (parseInt(requirement.value))) {
          playerSatisfiesAllRequirements = false;
        }
      }
      else {
        const positionGroupsPlayed = playerOverview.position_groups;
        positionGroupsPlayed.forEach(positionGroup => {
          const positionGroupStats = playerOverview.position_group_stats?.[positionGroup];
          if (!positionGroupStats || positionGroupStats?.custom_indexes?.[requirement.metric]?.confidence < (parseInt(requirement.value))) {
            playerSatisfiesAllRequirements = false;
          }
        });
      }
    });
    if (!playerSatisfiesAllRequirements) {
      return false;
    }
  }

  if (filters.form) {
    if (!filters.form.includes(playerOverview.form_status)) {
      return false;
    }
  }

  if (filters.playingTime) {
    if (!filters.playingTime.includes(playerOverview.availability_status)) {
      return false;
    }
  }

  if (filters.countryCodes) {
    if (!filters.countryCodes.includes(playerOverview.country_code)) {
      return false;
    }
  }

  if (filters.indexRequirements) {
    const positionGroupStats = playerOverview.position_group_stats?.[filters.positionGroup ?? 'OVERALL'];
    if (!positionGroupStats || filters.indexRequirements.some(
      requirement => {
        const value = requirement.metric === 'skill_index'
          ? positionGroupStats.skill_index?.index
          : positionGroupStats.custom_indexes?.[requirement.metric]?.index;
        return value && value < parseInt(requirement.value);
      }
    )) {
      return false;
    }
  }

  if (filters.logisticTeamIds && teams) {
    const playerIdsOnSelectedTeams = new Set<number>();

    // iterate over all teams
    Object.keys(teams).forEach(teamKey => {

      // check if team is selected
      if (filters.logisticTeamIds && (filters.logisticTeamIds[0] === 'allTeams' || filters.logisticTeamIds.includes(teamKey))) {
        const players = teams[teamKey]?.players ?? {};

        // iterate over positions and check if team has players in the position
        Object.keys(players).forEach(position => {
          const positionArray = players[position];

          // add player ids to set
          positionArray.forEach((player: PlayerId) => {
            const numberId = Number(player);
            if (!isNaN(numberId)) {
              playerIdsOnSelectedTeams.add(numberId);
            }
          });
        });
      }
    });

    if (!playerIdsOnSelectedTeams.has(playerOverview.id)) {
      return false;
    }
  }

  return true;
};
